<template>
  <div class="cashier" :class="{ 'is-paid': order && order.status === 1 }">
    <header class="cashier__head">
      <div class="cashier__brand">
        <img v-if="img('logo.png')" class="cashier__logo" :src="img('logo.png')" alt="" />
        <span v-else class="cashier__logo-ph" />
        <span class="cashier__brand-name">{{ $t('brand') }}</span>
      </div>
      <span class="cashier__title-mob" aria-hidden="true">{{ $t('brand') }}</span>
      <div class="cashier__head-lang">
        <img
          v-if="langIcon"
          class="cashier__lang-icon"
          :src="langIcon"
          alt=""
          role="button"
          tabindex="0"
          @click="langOpen = true"
          @keydown.enter="langOpen = true"
        />
        <i v-else class="cashier__lang-fallback el-icon-s-grid" @click="langOpen = true" />
      </div>
    </header>

    <div v-if="loading" class="cashier__loading">
      <i class="el-icon-loading" /> {{ $t('loading') }}
    </div>

    <div v-else-if="error" class="cashier__err">
      <p>{{ error }}</p>
    </div>

    <template v-else-if="order">
      <div class="cashier__main">
        <section class="cashier__card cashier__card--pay">
          <div class="cashier__card-top">
            <span class="cashier__order-id">{{ $t('orderNo') }} #{{ order.merchant_order_no }}</span>
            <span v-if="order.status === 0" class="cashier__timer">
              <img v-if="img('time.png')" class="cashier__time-icon" :src="img('time.png')" alt="" />
              <i v-else class="el-icon-time" />
              {{ countdownText }}
            </span>
            <span v-else class="cashier__st" :class="statusClass">{{ statusLabel }}</span>
          </div>
          <p class="cashier__hint">{{ $t('transferHint') }}</p>

          <div class="cashier__amt-row">
            <div>
              <div class="cashier__lab">{{ $t('rechargeAmount') }}</div>
              <div class="cashier__amt">
                <span class="cashier__amt-num">{{ displayAmount }}</span>
                <span class="cashier__amt-coin">{{ order.coin }}</span>
              </div>
            </div>
            <div class="cashier__net">
              <div class="cashier__net-line">
                <span class="cashier__dot" />
                <strong>{{ order.chain }}</strong>
              </div>
              <span class="cashier__net-sub">{{ chainSubLabel }}</span>
            </div>
          </div>

          <div class="cashier__qr-block cashier__qr-block--mob">
            <div class="cashier__qr-wrap">
              <img v-if="qrDataUrl" class="cashier__qr" :src="qrDataUrl" alt="QR" />
            </div>
            <p class="cashier__scan-tip">
              <span class="cashier__dot cashier__dot--green" />
              {{ $t('scanTip') }}
            </p>
          </div>

          <div class="cashier__addr-block">
            <div class="cashier__lab">{{ $t('rechargeAddress') }}</div>
            <div class="cashier__addr-box">
              <input class="cashier__addr-inp" readonly :value="order.address" />
              <button type="button" class="cashier__copy" @click="copyText(order.address)">
                <img v-if="img('copy.png')" class="cashier__copy-icon" :src="img('copy.png')" alt="" />
                <span>{{ $t('copy') }}</span>
              </button>
            </div>
          </div>
        </section>

        <section class="cashier__card cashier__card--qr">
          <div class="cashier__qr-block cashier__qr-block--desk">
            <div class="cashier__qr-wrap">
              <img v-if="qrDataUrl" class="cashier__qr" :src="qrDataUrl" alt="QR" />
            </div>
            <p class="cashier__scan-tip">
              <span class="cashier__dot cashier__dot--green" />
              {{ $t('scanTip') }}
            </p>
          </div>
        </section>
      </div>

      <section class="cashier__notice">
        <div class="cashier__notice-h">
          <img v-if="img('tips.png')" class="cashier__tips-icon" :src="img('tips.png')" alt="" />
          <i v-else class="el-icon-info" />
          {{ $t('noticeTitle') }}
        </div>
        <ol class="cashier__notice-list">
          <li><span class="cashier__notice-no">1</span><span>{{ $t('notice1') }}</span></li>
          <li><span class="cashier__notice-no">2</span><span>{{ noticeAmountLine }}</span></li>
          <li><span class="cashier__notice-no">3</span><span>{{ $t('notice3') }}</span></li>
          <li><span class="cashier__notice-no">4</span><span>{{ $t('notice4') }}</span></li>
        </ol>
      </section>
    </template>

    <!-- 语言：移动端底部抽屉 / PC 下拉 -->
    <div v-if="langOpen" class="cashier__lang-mask" @click.self="langOpen = false">
      <div class="cashier__lang-panel" :class="{ 'is-desktop': isDesktop }">
        <div class="cashier__lang-head">
          <span>{{ $t('selectLang') }}</span>
          <button type="button" @click="langOpen = false">
            <i class="el-icon-arrow-down" />
          </button>
        </div>
        <ul class="cashier__lang-list">
          <li
            v-for="loc in locales"
            :key="loc.id"
            :class="{ 'is-active': locale === loc.id }"
            @click="pickLang(loc.id)"
          >
            {{ loc.label }}
            <i v-if="locale === loc.id" class="el-icon-check" />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
import { cashierInit, cashierStatus } from '@/api/http'
import { i18n, getLocale, setLocale, applyLocaleFromUrl } from '@/i18n'

export default {
  name: 'CashierView',
  data() {
    return {
      loading: true,
      error: '',
      order: null,
      qrDataUrl: '',
      countdown: 0,
      timerId: null,
      pollId: null,
      langOpen: false,
      locale: getLocale(),
      locales: i18n.locales,
      isDesktop: typeof window !== 'undefined' && window.innerWidth >= 900,
      query: {}
    }
  },
  computed: {
    displayAmount() {
      if (!this.order) return '—'
      const v = parseFloat(this.order.amount_display)
      if (!Number.isFinite(v)) return this.order.amount_display
      const s = v.toFixed(8).replace(/\.?0+$/, '')
      return s || '0'
    },
    countdownText() {
      const m = Math.floor(this.countdown / 60)
      const s = this.countdown % 60
      return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    },
    statusLabel() {
      if (!this.order) return ''
      const map = { 0: this.$t('pending'), 1: this.$t('paid'), 2: this.$t('timeout') }
      return map[this.order.status] || ''
    },
    statusClass() {
      if (!this.order) return ''
      return { 0: 'is-pend', 1: 'is-ok', 2: 'is-out' }[this.order.status] || ''
    },
    noticeAmountLine() {
      return this.$t('notice2')
        .replace('{amount}', this.displayAmount)
        .replace('{coin}', this.order ? this.order.coin : 'USDT')
    },
    langIcon() {
      return this.img('language.png')
    },
    chainSubLabel() {
      if (!this.order) return ''
      const chain = (this.order.chain || '').toUpperCase()
      const map = {
        TRC20: 'Tron',
        ERC20: 'Ethereum',
        BSC: 'BSC',
        BTC: 'Bitcoin',
        ETH: 'Ethereum'
      }
      const net = map[chain] || chain
      return `${net} · ${this.$t('etaVal')}`
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    this.parseQuery()
    this.bootstrap()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    this.clearTimers()
  },
  methods: {
    img(name) {
      try {
        return require(`@/image/${name}`)
      } catch (e) {
        return ''
      }
    },
    onResize() {
      this.isDesktop = window.innerWidth >= 900
    },
    parseQuery() {
      const q = this.$route.query
      this.query = {
        sign: (q.sign || '').trim()
      }
      const lang = (q.lang || '').trim()
      if (lang) {
        applyLocaleFromUrl(lang)
        this.locale = getLocale()
      }
    },
    async bootstrap() {
      if (!this.query.sign) {
        this.error = this.$t('paramError')
        this.loading = false
        return
      }
      try {
        const r = await cashierInit(this.query)
        this.order = r.data
        this.countdown = r.data.expire_seconds || 0
        await this.renderQr(r.data.address)
        this.startTimers()
      } catch (e) {
        this.error = (e && e.msg) || this.$t('initFail')
      } finally {
        this.loading = false
      }
    },
    async renderQr(address) {
      if (!address) return
      const size = this.isDesktop ? 260 : 240
      this.qrDataUrl = await QRCode.toDataURL(address, {
        width: size,
        margin: 1,
        color: { dark: '#000000', light: '#ffffff' }
      })
    },
    startTimers() {
      this.clearTimers()
      this.timerId = setInterval(() => {
        if (this.countdown > 0) this.countdown--
      }, 1000)
      this.pollId = setInterval(() => this.pollStatus(), 5000)
    },
    clearTimers() {
      if (this.timerId) clearInterval(this.timerId)
      if (this.pollId) clearInterval(this.pollId)
      this.timerId = null
      this.pollId = null
    },
    async pollStatus() {
      if (!this.order || this.order.status !== 0) return
      try {
        const r = await cashierStatus({
          pay_order_no: this.order.pay_order_no
        })
        this.order = r.data
        this.countdown = r.data.expire_seconds || 0
        if (r.data.status === 1) {
          this.$message.success(this.$t('paidSuccess'))
          this.clearTimers()
        } else if (r.data.status === 2) {
          this.$message.warning(this.$t('timedOut'))
          this.clearTimers()
        }
      } catch (e) {
        /* 静默 */
      }
    },
    pickLang(id) {
      setLocale(id)
      this.locale = id
      this.langOpen = false
      this.$forceUpdate()
    },
    copyText(t) {
      const s = (t || '').toString()
      if (!s) return
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(s).then(() => this.$message.success(this.$t('copied')))
        return
      }
      this.$message.warning(this.$t('copyFail'))
    }
  }
}
</script>

<style lang="scss" scoped>
$c-bg: #0b0e14;
$c-card: #1a1f2e;
$c-text: #e8eaef;
$c-muted: #8b92a8;
$c-green: #22c55e;
$c-yellow: #facc15;

$bp-desktop: 900px;

.cashier {
  min-height: 100vh;
  padding: calc(env(safe-area-inset-top, 44px) + 16px) 16px 32px;
  color: $c-text;
  box-sizing: border-box;
  max-width: 480px;
  margin: 0 auto;

  @media (min-width: $bp-desktop) {
    max-width: 1100px;
    padding: 32px 32px 48px;
  }
}

/* 顶栏：左 logo / 中标题 / 右语言，同一行垂直居中 */
.cashier__head {
  display: flex;
  align-items: center;
  min-height: 32px;
  margin-bottom: 20px;
  gap: 8px;

  @media (min-width: $bp-desktop) {
    min-height: 40px;
    margin-bottom: 24px;
    justify-content: space-between;
  }
}

.cashier__brand {
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 32px;

  @media (min-width: $bp-desktop) {
    flex: 0 1 auto;
  }
}

.cashier__brand-name {
  display: none;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  line-height: 1;

  @media (min-width: $bp-desktop) {
    display: inline;
  }
}

.cashier__title-mob {
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 0.02em;
  line-height: 32px;
  pointer-events: none;

  @media (min-width: $bp-desktop) {
    display: none;
  }
}

.cashier__head-lang {
  flex: 0 0 32px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 32px;
}

.cashier__logo,
.cashier__logo-ph {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.cashier__logo-ph {
  border-radius: 8px;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
}

.cashier__lang-icon {
  width: 32px;
  height: 32px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
  cursor: pointer;
  border: none;
  background: none;
  padding: 0;
  vertical-align: middle;
}

.cashier__lang-fallback {
  width: 32px;
  height: 32px;
  line-height: 32px;
  text-align: center;
  font-size: 20px;
  cursor: pointer;
  color: $c-muted;
}

.cashier__loading,
.cashier__err {
  text-align: center;
  padding: 48px 16px;
  color: $c-muted;
}

.cashier__main {
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (min-width: $bp-desktop) {
    flex-direction: row;
    align-items: stretch;
    gap: 20px;
  }
}

.cashier__card {
  background: $c-card;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  padding: 18px 16px 20px;
  position: relative;
  overflow: hidden;
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #6366f1, #8b5cf6, #22d3ee);
  }
}

.cashier__card--pay {
  flex: 1;

  @media (min-width: $bp-desktop) {
    flex: 1.15;
    padding: 22px 24px 24px;
  }
}

.cashier__card--qr {
  display: none;

  @media (min-width: $bp-desktop) {
    display: flex;
    flex: 0.85;
    align-items: center;
    justify-content: center;
    padding: 28px 24px;
    min-height: 360px;
  }
}

.cashier__card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.cashier__order-id {
  font-size: 12px;
  color: $c-muted;
}

.cashier__timer {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 20px;
  background: rgba(250, 204, 21, 0.22);
  color: $c-yellow;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
}

.cashier__time-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.cashier__st {
  font-size: 13px;
  font-weight: 600;
  &.is-ok {
    color: $c-green;
  }
  &.is-out {
    color: #f87171;
  }
  &.is-pend {
    color: $c-yellow;
  }
}

.cashier__hint {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.45;
  color: #f3f4f6;

  @media (min-width: $bp-desktop) {
    margin-bottom: 20px;
    font-size: 16px;
  }
}

.cashier__lab {
  font-size: 12px;
  color: $c-muted;
  margin-bottom: 6px;
}

.cashier__amt-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 16px;
}

.cashier__amt-num {
  font-size: 36px;
  font-weight: 700;
  line-height: 1.1;

  @media (min-width: $bp-desktop) {
    font-size: 40px;
  }
}

.cashier__amt-coin {
  margin-left: 8px;
  font-size: 18px;
  color: $c-green;
  font-weight: 600;
}

.cashier__net {
  text-align: right;
  font-size: 13px;
  flex-shrink: 0;
  padding-bottom: 6px;
}

.cashier__net-line {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  strong {
    color: $c-text;
    font-weight: 600;
    font-size: 14px;
  }
}

.cashier__net-sub {
  display: block;
  font-size: 11px;
  color: $c-muted;
  margin-top: 4px;
  white-space: nowrap;
}

.cashier__dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f87171;
  flex-shrink: 0;
  &--green {
    background: $c-green;
  }
}

.cashier__addr-box {
  display: flex;
  align-items: center;
  gap: 6px;
  min-height: 44px;
  padding: 4px 4px 4px 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  background: #0f131c;
  box-sizing: border-box;
}

.cashier__addr-inp {
  flex: 1;
  min-width: 0;
  height: 36px;
  padding: 0;
  border: none;
  background: transparent;
  color: $c-text;
  font-size: 12px;
  line-height: 1.3;
  outline: none;

  @media (min-width: $bp-desktop) {
    font-size: 13px;
  }
}

.cashier__copy {
  flex-shrink: 0;
  height: 36px;
  padding: 0 10px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(180deg, #5b5bd6 0%, #4f46e5 100%);
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.cashier__copy-icon {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}

.cashier__qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;

  &--mob {
    margin: 8px 0 22px;

    @media (min-width: $bp-desktop) {
      display: none;
    }
  }

  &--desk {
    display: none;

    @media (min-width: $bp-desktop) {
      display: flex;
      justify-content: center;
      margin: 0;
    }
  }
}

.cashier__qr-wrap {
  background: #fff;
  border-radius: 16px;
  padding: 14px;
  line-height: 0;
  box-sizing: border-box;
}

.cashier__qr {
  display: block;
  width: 200px;
  height: 200px;
  margin: 0 auto;

  @media (min-width: $bp-desktop) {
    width: 240px;
    height: 240px;
  }
}

.cashier__scan-tip {
  margin: 12px 0 0;
  text-align: center;
  font-size: 13px;
  color: $c-muted;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
}

.cashier__notice {
  margin-top: 16px;
  padding: 16px;
  border-radius: 16px;
  background: $c-card;
  border: 1px solid rgba(255, 255, 255, 0.06);

  @media (min-width: $bp-desktop) {
    margin-top: 20px;
    padding: 20px 24px 24px;
  }
}

.cashier__notice-h {
  color: #818cf8;
  font-weight: 600;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.cashier__tips-icon {
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
}

.cashier__notice-list {
  margin: 0;
  padding: 0;
  list-style: none;
  color: $c-muted;
  font-size: 12px;
  line-height: 1.65;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @media (min-width: $bp-desktop) {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    font-size: 13px;
  }

  li {
    margin: 0;
    display: flex;
    align-items: flex-start;
    gap: 10px;

    @media (min-width: $bp-desktop) {
      padding: 14px 14px 14px 12px;
      background: rgba(0, 0, 0, 0.22);
      border: 1px solid rgba(255, 255, 255, 0.05);
      border-radius: 12px;
    }
  }
}

.cashier__notice-no {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: rgba(99, 102, 241, 0.35);
  color: #a5b4fc;
  font-size: 12px;
  font-weight: 600;
  line-height: 22px;
  text-align: center;
}

.cashier__lang-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.cashier__lang-panel {
  width: 100%;
  max-width: 480px;
  background: #1a1f2e;
  border-radius: 16px 16px 0 0;
  padding: 16px 0 24px;
  max-height: 70vh;
  overflow: auto;
  &.is-desktop {
    position: absolute;
    top: 72px;
    right: 32px;
    width: 220px;
    border-radius: 12px;
    max-height: 360px;
    align-self: flex-start;
  }
}

.cashier__lang-mask:has(.is-desktop) {
  align-items: flex-start;
  justify-content: flex-end;
  background: transparent;
}

.cashier__lang-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 20px 12px;
  font-weight: 600;
  button {
    border: none;
    background: none;
    color: $c-muted;
    cursor: pointer;
    font-size: 18px;
  }
}

.cashier__lang-list {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    padding: 14px 20px;
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &.is-active {
      background: rgba(99, 102, 241, 0.35);
      color: #fff;
    }
  }
}
</style>
